import { Fullscreen } from "@shopify/app-bridge/actions"
import { Modal as polarisModal, SkeletonBodyText } from "@shopify/polaris"
import { Button, ButtonGroup, FullscreenBar } from "@shopify/polaris"
import classNames from "classnames"
import { cloneDeep } from "lodash-es"
import { Observer, observer } from "mobx-react"
import { useCallback, useEffect, useState } from "react"
import { useHistory } from "react-router-dom"

import APIWorkFlow from "@/api/workFlow"
import { useSaveBar } from "@/common/hooks"
import { ConfirmModal } from "@/components"
import stores from "@/stores"
import AppBridge, { isShopifyEmbedded } from "@/utils/app-bridge"
import { elePosition } from "@/utils/elePosition"
import toast from "@/utils/toast"
import workflowPopupsUtilsExample from "@/utils/workflowPopupsUtils"

import styles from "./index.module.scss"

function WorkflowFullscreenBar() {
  const { WorkflowStore } = stores
  const { push } = useHistory()

  // 取消修改弹窗
  const [discardPopup, setDiscardPopup] = useState(false)

  const [fullscreenLoading, setFullscreenLoading] = useState(false)

  useEffect(() => {
    // 工作流详情则延迟展示
    const urlParams = new URLSearchParams(window.location.search)
    const workflowId = urlParams.get("id")
    if (workflowId) {
      setFullscreenLoading(true)
      setTimeout(() => {
        setFullscreenLoading(false)
      }, 400)
    }
  }, [])

  if (isShopifyEmbedded()) {
    // 监听shopfiy弹窗的关闭事件
    if (AppBridge.app()) {
      useEffect(() => {
        const unsubscribe = AppBridge.app().subscribe(
          Fullscreen.Action.EXIT,
          function () {
            handleActionClick()
            // AppBridge.redirect().dispatch(
            //   Redirect.Action.APP,
            //   "/settings/automation",
            // )
          },
        )
        return () => {
          unsubscribe()
        }
      }, [])
    }
  }

  // 处理 Exit 按钮事件
  const handleActionClick = useCallback(() => {
    WorkflowStore.setCheckedList("isCheckWorkflowName", true)
    WorkflowStore.setCheckedList("isBlurCheckName", true)
    WorkflowStore.setPopupList("workflowPopup", true)
    // 点击退出事件
    // 如果 Save 按钮是禁用状态，就返回到上一页
    if (!WorkflowStore.popupSaveBtn.active) {
      goBack()
    } else {
      setDiscardPopup(true)
      // 重新进入到编辑弹窗
      AppBridge.dispatchFullscreen(Fullscreen.Action.ENTER)
    }
  }, [WorkflowStore.popupSaveBtn.active])

  // 过审调整 start
  const onDiscardAction = () => {
    const _defaultPopupOptions = workflowPopupsUtilsExample.getInitOptions()
    const { info, workFlowAction, workflowRules } = _defaultPopupOptions || {}
    WorkflowStore.setInfoList(info)
    WorkflowStore.setWorkFlowActionList(workFlowAction)
    WorkflowStore.setWorkflowRules(workflowRules)
    // workflowPopupsUtilsExample.destroyListener() // 先销毁监听器
    // workflowPopupsUtilsExample.registerListener() // 最后再注册监听器
    WorkflowStore.setCheckedList("isCheckWorkflowName", true)
    WorkflowStore.setCheckedList("isBlurCheckName", true)
  }
  // 判断当前loading是保存按钮还是active或者turn off按钮
  const isLoading = (type, val = false) => {
    if (type) {
      WorkflowStore.setPopupSaveBtn("activateLoading", val)
    } else {
      WorkflowStore.setPopupSaveBtn("loading", val)
    }
  }

  // 保存时，如果校验结果不通过，右边没有弹窗的话要弹出右边的框
  const showWorkflowFormModal = () => {
    if (!WorkflowStore.popupList.workflowPopup) {
      WorkflowStore.setPopupList("workflowPopup", true)
    }
    setTimeout(() => {
      workflowPopupsUtilsExample.initSetActive(false)
    }, 200)
  }

  const onSaveAction = async (e, type?) => {
    WorkflowStore.setIsWorkflowSaveBtn(true)
    // 如果校验失败则不进行保存
    // activate或者turn off按钮逻辑处理
    const { id: workflowId, name, kind, logical, enable } = WorkflowStore.info
    if (!name) {
      const _info = { ...WorkflowStore.info, isCheckRule: true }
      WorkflowStore.setInfoList(_info)
      // 定位到该报错的位置
      elePosition({ eleById: "workflow-name", beHavior: "smooth" })
      showWorkflowFormModal()
      return
    }
    isLoading(type, true)

    // 校验表单rule数据(只需要再logical为and或者是or的时候才需要)
    if ([1, 2].includes(logical)) {
      const _workflowRules = WorkflowStore.workflowRules
      const isCheckRule = _workflowRules.some(
        (item) =>
          !item.rule_type || !item.rule_value || item.rule_value === "0",
      )
      if (isCheckRule) {
        // 判断rule第几个未填写
        const checkIndex = _workflowRules.findIndex(
          (item) =>
            !item.rule_type || !item.rule_value || item.rule_value === "0",
        )
        // 定位到该报错的位置
        elePosition({
          eleById: `workflow-conditions-${checkIndex + 1}`,
          beHavior: "smooth",
        })
        WorkflowStore.setInfo("isCheckRule", true)
        isLoading(type)
        showWorkflowFormModal()
        return
      }
    }

    // 保存如果不是草稿，都需要校验；如果是点击激活状态也需要校验
    if (WorkflowStore.info.enable !== 1 || type) {
      const { return_address_id, package_id, carrier_option, logistics } =
        WorkflowStore.workFlowAction
      if (
        kind === 1 && // 自动同意需要校验的输入框字段
        !(
          return_address_id &&
          package_id &&
          carrier_option &&
          kind &&
          logistics
        )
      ) {
        WorkflowStore.setInfo("isCheckAction", true)
        // 定位到action的位置
        elePosition({
          eleById: "workflow_carrier_service_option",
          beHavior: "smooth",
        })
        isLoading(type)
        showWorkflowFormModal()
        return
      }
    }

    // 调用保存按钮
    const { workFlowAction, workflowRules } = WorkflowStore
    const _workflowRules = workflowRules.map((item) => {
      return {
        ...item,
        rule_value: item?.rule_value
          ?.split(",")
          ?.filter((item) => item !== "0")
          ?.join(","),
      }
    })
    const _enable = type ? ([1, 3].includes(enable) ? 2 : 3) : enable
    const nextWorkflowRules = logical === 3 ? [] : _workflowRules
    const _logistics =
      typeof workFlowAction?.logistics !== "string"
        ? workFlowAction.logistics?.toString()
        : workFlowAction.logistics
    const param = {
      id: workflowId,
      name,
      kind,
      logical,
      enable: _enable,
      action: { ...workFlowAction, logistics: _logistics },
      rules: nextWorkflowRules,
    }
    const { code, data: resData } = await APIWorkFlow.saveWorkFlow(param)
    if (code === 1000) {
      const { id: resId } = resData
      WorkflowStore.setInfo("id", resId)
      WorkflowStore.setInfo("enable", _enable)
      const _info = {
        id: resId,
        name: resData.name,
        kind: resData.kind,
        logical: resData.logical,
        enable: resData.enable,
        isCheckRule: WorkflowStore.info.isCheckRule,
        isCheckAction: WorkflowStore.info.isCheckAction,
      }
      const _defaultPopupOptions = {
        info: _info,
        workFlowAction: { ...workFlowAction },
        workflowRules: [...workflowRules],
      }
      workflowPopupsUtilsExample.setInitOptions(_defaultPopupOptions)
      // workflowPopupsUtilsExample.registerListener()
      WorkflowStore.setHasTemplate(false)
    } else if (code === 2001) {
      // 提示工作流名称重复问题
      WorkflowStore.setCheckedList("isCheckWorkflowName", false)
      // 定位到该报错的位置
      elePosition({ eleById: "workflow-name", beHavior: "smooth" })
    } else {
      WorkflowStore.setCheckedList("isCheckWorkflowName", true)
    }
    setTimeout(() => {
      workflowPopupsUtilsExample.initSetActive(false)
      isLoading(type, false)
    }, 1000)
  }

  const setInitWorkflowData = () => {
    const _info = {
      id: 0,
      name: "",
      kind: 0,
      logical: 1,
      enable: 1,
      isCheckRule: false,
      isCheckAction: false,
    }
    const _workFlowAction = {
      return_address_id: 0,
      package_id: 0,
      logistics: 0,
      carrier_option: 0,
    }
    const _workflowRules = [
      {
        rule_type: 0,
        rule_com: 1,
        rule_value: "",
      },
    ]
    WorkflowStore.setWorkflowRules(_workflowRules)
    WorkflowStore.setWorkFlowActionList(_workFlowAction)
    WorkflowStore.setInfoList(_info)
  }

  // 返回
  const goBack = () => {
    push("/settings/automation")
    // 初始化workflow数据
    setInitWorkflowData()
    const _curActiveNode = {
      index: 0,
      label: "",
      title: "",
    }
    // 还原node高亮的数据
    WorkflowStore.setCurActiveNode(_curActiveNode)
    WorkflowStore.setHasTemplate(false)
    const _flowByNodeStatus = {
      isHeightLight: {
        index: 0,
        status: !WorkflowStore.flowByNodeStatus.isHeightLight.status,
      },
      isShowDropdown: !WorkflowStore.flowByNodeStatus.isShowDropdown,
    }
    WorkflowStore.setFlowByNodeStatus(_flowByNodeStatus)
  }

  // 过审调整 end

  const handleConfirm = () => {
    const { initWorkflowOptions } = WorkflowStore
    const _initWorkflowOptions = cloneDeep(initWorkflowOptions)
    const { info, workFlowAction, workflowRules } = _initWorkflowOptions
    WorkflowStore.setInfoList(info)
    WorkflowStore.setWorkFlowActionList(workFlowAction)
    WorkflowStore.setWorkflowRules(workflowRules)
    // workflowPopupsUtilsExample.destroyListener() // 先销毁监听器
    // workflowPopupsUtilsExample.registerListener() // 最后再注册监听器
    setDiscardPopup(false)
    goBack()
  }

  // 处理runtest按钮
  // 逻辑：需要校验名称、condition和action
  const handleRunTest = async () => {
    const { id: workflowId, name, kind, logical } = WorkflowStore.info
    if (!name) {
      const _info = { ...WorkflowStore.info, isCheckRule: true }
      WorkflowStore.setInfoList(_info)
      // 定位到该报错的位置
      elePosition({ eleById: "workflow-name", beHavior: "smooth" })
      showWorkflowFormModal()
      return
    }
    // 校验表单rule数据
    if ([1, 2].includes(logical)) {
      const _workflowRules = WorkflowStore.workflowRules
      const isCheckRule = _workflowRules.some(
        (item) =>
          !item.rule_type || !item.rule_value || item.rule_value === "0",
      )
      if (isCheckRule) {
        // 判断rule第几个未填写
        const checkIndex = _workflowRules.findIndex(
          (item) =>
            !item.rule_type || !item.rule_value || item.rule_value === "0",
        )
        // 定位到该报错的位置
        elePosition({
          eleById: `workflow-conditions-${checkIndex + 1}`,
          beHavior: "smooth",
        })
        WorkflowStore.setInfo("isCheckRule", true)
        showWorkflowFormModal()
        return
      }
    }

    const { return_address_id, package_id, carrier_option, logistics } =
      WorkflowStore.workFlowAction
    if (
      kind === 1 &&
      !(return_address_id && package_id && carrier_option && kind && logistics)
      // exchange换货类型不需要校验这几个字段
    ) {
      // 定位到action的位置
      // elePosition({ eleById: "workflow-action", beHavior: "smooth" })
      elePosition({
        eleById: "workflow_carrier_service_option",
        beHavior: "smooth",
      })
      WorkflowStore.setInfo("isCheckAction", true)
      showWorkflowFormModal()
      return
    }

    WorkflowStore.setPopupList("runTestPopup", true)
  }

  const { node } = useSaveBar({
    onSave: onSaveAction,
    onDiscard: onDiscardAction,
    saving: WorkflowStore.popupSaveBtn.loading,
    isChanged: WorkflowStore.popupSaveBtn.active || WorkflowStore.hasTemplate,
    confirmDisabled: !WorkflowStore.popupSaveBtn.active,
  })
  return (
    <Observer>
      {() => {
        return (
          <>
            <ConfirmModal
              title={t("DiscardChanges")}
              open={discardPopup}
              onClose={() => setDiscardPopup(false)}
              disabledConfirm={false}
              confirmText={t("LeavePage")}
              cancelText={t("Stay")}
              destructiveConfirm
              onConfirm={async () => handleConfirm()}
            >
              <polarisModal.Section>
                <div>{t("AreYouSureYouWantToDiscardAllUns")}</div>
              </polarisModal.Section>
            </ConfirmModal>
            {/* shopfiy的顶部导航条 */}
            {fullscreenLoading ? (
              <div className="bg-[#fff] p-3">
                <SkeletonBodyText lines={2} />
              </div>
            ) : (
              <div>
                {node}
                <FullscreenBar onAction={handleActionClick}>
                  <div
                    className={classNames(
                      "flex items-center justify-between pl-[1rem] pr-[1rem]",
                      styles.fullscreenBarWrapper,
                    )}
                    style={{
                      flexGrow: 1,
                    }}
                  >
                    <div>
                      <div
                        className={classNames(
                          "bg-[#A4E8F2] p-2 pt-0 pb-[1px] rounded-lg mt-[-9px] text-[12px]",
                          {
                            "bg-[#AEE9D1]": WorkflowStore.info?.enable === 2,
                            "bg-[#A4E8F2]": WorkflowStore.info?.enable === 1,
                            "bg-[#E4E5E7]": WorkflowStore.info?.enable === 3,
                          },
                        )}
                      >
                        {WorkflowStore.info?.enable === 1
                          ? t("Draft")
                          : WorkflowStore.info?.enable === 2
                            ? t("Active")
                            : t("Inactive")}
                      </div>
                      <ButtonGroup>
                        {/* <div
                    className={classNames(
                      "bg-[#A4E8F2] p-2 pt-[1px] pb-[1px] rounded-lg mr-3",
                      {
                        "bg-[#3f534b]": WorkflowStore.info?.enable === 2,
                        "bg-[#A4E8F2]": WorkflowStore.info?.enable === 1,
                        "bg-[#E4E5E7]": WorkflowStore.info?.enable === 3,
                      },
                    )}
                  >
                    {WorkflowStore.info?.enable === 1
                      ? "Draft"
                      : WorkflowStore.info?.enable === 2
                        ? "Active"
                        : "Inactive"}
                  </div>
                  <Button
                    disabled={
                      !WorkflowStore.popupSaveBtn.active ||
                      WorkflowStore.popupSaveBtn.activateLoading
                    }
                    onClick={onDiscardAction}
                  >
                    Discard
                  </Button> */}
                        {/* 保存按钮后再进行校验，无需diasbled禁用处理 */}
                        {/* <Button
                    disabled={
                      (!WorkflowStore.popupSaveBtn.active ||
                        WorkflowStore.popupSaveBtn.activateLoading) &&
                      !WorkflowStore.hasTemplate
                    }
                    primary
                    loading={WorkflowStore.popupSaveBtn.loading}
                    onClick={() => onSaveAction()}
                  >
                    Save
                  </Button> */}
                      </ButtonGroup>
                    </div>
                    {/* 以下两个按钮，如果当前的工作流无action的情况禁用，并且需要做校验 */}
                    <ButtonGroup>
                      <Button
                        disabled={
                          !WorkflowStore.info.id ||
                          WorkflowStore.info.kind === 0 ||
                          WorkflowStore.popupSaveBtn.active ||
                          (!WorkflowStore.hasApproveAction.status &&
                            WorkflowStore.hasApproveAction.id !==
                              WorkflowStore.info.id)
                        }
                        // !WorkflowStore.hasExchangeAction.status &&
                        //     WorkflowStore.hasExchangeAction.id !==
                        //       WorkflowStore.info.id
                        onClick={() => handleRunTest()}
                      >
                        {t("RunTest")}
                      </Button>
                      {/* 如果当前状态是草稿或者是未激活状态，按钮的文本是Activate，如果是激活状态，则显示 Turn off */}
                      <Button
                        loading={WorkflowStore.popupSaveBtn.activateLoading}
                        onClick={(e) => onSaveAction(e, "activate")}
                        disabled={
                          !WorkflowStore.info.id ||
                          WorkflowStore.info.kind === 0 ||
                          WorkflowStore.popupSaveBtn.active ||
                          (!WorkflowStore.hasApproveAction.status &&
                            WorkflowStore.hasApproveAction.id !==
                              WorkflowStore.info.id)
                        }
                        // !WorkflowStore.hasExchangeAction.status &&
                        //     WorkflowStore.hasExchangeAction.id !==
                        //       WorkflowStore.info.id
                        variant="primary"
                      >
                        {WorkflowStore.info.enable === 2
                          ? t("TurnOff")
                          : t("Activate")}
                      </Button>
                    </ButtonGroup>
                  </div>
                </FullscreenBar>
              </div>
            )}
          </>
        )
      }}
    </Observer>
  )
}

export default observer(WorkflowFullscreenBar)
